import { useContext, useEffect, useState } from "react";
import { API } from "../../../api";
import { TeamMember } from "../../../models";
import { UserContext } from "../../../hooks/UserContext";
import { generateRandomPassword } from "./PasswordGenerator.helpers";

export const usePasswordGeneratorViewModel = () => {
	const currentUser = useContext(UserContext);
	const isSuperUser = currentUser?.isAdmin ?? false;

	const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
	const [selectedUser, setSelectedUser] = useState<TeamMember | null>(null);
	const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
	const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (isSuperUser) {
			fetchTeamMembers();
		}
	}, [isSuperUser]);

	const fetchTeamMembers = async () => {
		setLoadingTeamMembers(true);
		setError(null);
		try {
			const members = await API.Data.TeamMembers.get.byFilter({
				type: "AND",
				fields: [{ status: "Active" }, { password: { operator: "is_empty" } }],
			});
			members.sort((a, b) => a.name.localeCompare(b.name));
			setTeamMembers(members);
		} catch (err: any) {
			setError(`Failed to load team members: ${err.message || "Unknown error"}`);
		} finally {
			setLoadingTeamMembers(false);
		}
	};

	const generateAndSave = async () => {
		if (!selectedUser) return;

		setSaving(true);
		setSaved(false);
		setError(null);
		setGeneratedPassword(null);

		try {
			const password = generateRandomPassword();
			await API.Data.TeamMembers.setPassword(selectedUser.id, password);
			setGeneratedPassword(password);
			setSaved(true);
		} catch (err: any) {
			setError(`Failed to save password: ${err.message || "Unknown error"}`);
		} finally {
			setSaving(false);
		}
	};

	return {
		isSuperUser,
		teamMembers,
		selectedUser,
		setSelectedUser,
		loadingTeamMembers,
		generatedPassword,
		saving,
		saved,
		error,
		generateAndSave,
	};
};
